import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiMapPin, FiUsers, FiDollarSign, FiCalendar, FiTarget, FiBarChart, FiCheck } = FiIcons;

const CandidateAdvertising = () => {
  const [selectedPackage, setSelectedPackage] = useState('district');
  const [adForm, setAdForm] = useState({
    candidateName: '',
    position: '',
    district: '',
    targetAudience: 'district',
    budget: '',
    duration: '30',
    message: ''
  });

  const packages = [
    {
      id: 'local',
      name: 'Local Campaign',
      price: 299,
      duration: '30 days',
      features: [
        'City/County targeting',
        'Up to 10,000 impressions',
        'Basic analytics',
        'Mobile-optimized ads',
        'Email support'
      ],
      recommended: false
    },
    {
      id: 'district',
      name: 'District Campaign',
      price: 799,
      duration: '30 days',
      features: [
        'State/Congressional district targeting',
        'Up to 50,000 impressions',
        'Advanced analytics dashboard',
        'A/B testing capabilities',
        'Priority support',
        'Social media integration'
      ],
      recommended: true
    },
    {
      id: 'state',
      name: 'Statewide Campaign',
      price: 1999,
      duration: '30 days',
      features: [
        'Full state targeting',
        'Up to 200,000 impressions',
        'Premium analytics suite',
        'Multiple ad formats',
        'Dedicated account manager',
        'Custom landing pages',
        'Video ad support'
      ],
      recommended: false
    },
    {
      id: 'federal',
      name: 'Federal Campaign',
      price: 4999,
      duration: '30 days',
      features: [
        'National targeting',
        'Unlimited impressions',
        'Complete analytics suite',
        'All ad formats included',
        'White-glove service',
        'Custom integrations',
        'Real-time optimization',
        'Debate night boosts'
      ],
      recommended: false
    }
  ];

  const targetingOptions = [
    {
      id: 'local',
      name: 'Local (City/County)',
      description: 'Target voters in your city or county',
      reach: '5K - 50K',
      icon: FiMapPin
    },
    {
      id: 'district',
      name: 'District',
      description: 'Target specific legislative or congressional districts',
      reach: '50K - 500K',
      icon: FiTarget
    },
    {
      id: 'state',
      name: 'Statewide',
      description: 'Reach voters across your entire state',
      reach: '500K - 5M',
      icon: FiTrendingUp
    },
    {
      id: 'federal',
      name: 'Federal',
      description: 'National reach for federal campaigns',
      reach: '5M+',
      icon: FiUsers
    }
  ];

  const handleInputChange = (e) => {
    setAdForm({
      ...adForm,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Ad campaign submission:', adForm, selectedPackage);
  };

  const PackageCard = ({ pkg, isSelected, onSelect }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-300 ${
        isSelected ? 'ring-2 ring-patriot-red border-patriot-red' : 'hover:shadow-lg'
      } ${pkg.recommended ? 'border-2 border-patriot-gold' : ''}`}
      onClick={() => onSelect(pkg.id)}
    >
      {pkg.recommended && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-patriot-gold text-patriot-navy px-3 py-1 rounded-full text-sm font-bold">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-patriot-navy mb-2">{pkg.name}</h3>
        <div className="text-3xl font-bold text-patriot-red mb-1">
          ${pkg.price}
        </div>
        <p className="text-gray-600 text-sm">{pkg.duration}</p>
      </div>
      
      <ul className="space-y-3 mb-6">
        {pkg.features.map((feature, index) => (
          <li key={index} className="flex items-center text-sm">
            <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      
      <button
        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
          isSelected
            ? 'bg-patriot-red text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {isSelected ? 'Selected' : 'Select Package'}
      </button>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-patriot-navy mb-4">Candidate Advertising</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Reach conservative voters where they are most engaged. Advertise your campaign 
            directly to patriots in your district, state, or nationwide.
          </p>
        </motion.div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-8 mb-12"
        >
          <h2 className="text-2xl font-bold text-patriot-navy mb-6 text-center">
            Why Advertise on PatriotHub?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <SafeIcon icon={FiUsers} className="h-12 w-12 text-patriot-red mx-auto mb-4" />
              <h3 className="font-semibold text-patriot-navy mb-2">Targeted Conservative Audience</h3>
              <p className="text-gray-600 text-sm">
                Reach 50,000+ engaged conservative voters who are actively involved in politics
              </p>
            </div>
            <div className="text-center">
              <SafeIcon icon={FiTarget} className="h-12 w-12 text-patriot-red mx-auto mb-4" />
              <h3 className="font-semibold text-patriot-navy mb-2">Precise Geographic Targeting</h3>
              <p className="text-gray-600 text-sm">
                Target voters by district, county, state, or nationally based on your campaign needs
              </p>
            </div>
            <div className="text-center">
              <SafeIcon icon={FiBarChart} className="h-12 w-12 text-patriot-red mx-auto mb-4" />
              <h3 className="font-semibold text-patriot-navy mb-2">Detailed Analytics</h3>
              <p className="text-gray-600 text-sm">
                Track impressions, clicks, and engagement to optimize your campaign performance
              </p>
            </div>
          </div>
        </motion.div>

        {/* Targeting Options */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-patriot-navy mb-6 text-center">
            Choose Your Targeting
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {targetingOptions.map((option) => (
              <div
                key={option.id}
                className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow duration-300"
              >
                <SafeIcon icon={option.icon} className="h-8 w-8 text-patriot-red mx-auto mb-3" />
                <h3 className="font-semibold text-patriot-navy mb-2">{option.name}</h3>
                <p className="text-gray-600 text-sm mb-2">{option.description}</p>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  Reach: {option.reach}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Packages */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-patriot-navy mb-8 text-center">
            Select Your Campaign Package
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                isSelected={selectedPackage === pkg.id}
                onSelect={setSelectedPackage}
              />
            ))}
          </div>
        </motion.div>

        {/* Campaign Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-8"
        >
          <h2 className="text-2xl font-bold text-patriot-navy mb-6">
            Start Your Campaign
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Candidate Name
                </label>
                <input
                  type="text"
                  name="candidateName"
                  value={adForm.candidateName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                  placeholder="Enter candidate name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position/Office
                </label>
                <input
                  type="text"
                  name="position"
                  value={adForm.position}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                  placeholder="e.g., U.S. Senate, Governor, Mayor"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  District/Area
                </label>
                <input
                  type="text"
                  name="district"
                  value={adForm.district}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                  placeholder="e.g., Texas 7th District, Harris County"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Duration
                </label>
                <select
                  name="duration"
                  value={adForm.duration}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                >
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="custom">Custom duration</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Message
              </label>
              <textarea
                name="message"
                value={adForm.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-patriot-red focus:border-transparent"
                placeholder="Enter your campaign message (max 500 characters)"
                maxLength={500}
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                {adForm.message.length}/500 characters
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-patriot-navy mb-4">Campaign Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Package:</span>
                  <p className="font-medium">
                    {packages.find(p => p.id === selectedPackage)?.name}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Duration:</span>
                  <p className="font-medium">{adForm.duration} days</p>
                </div>
                <div>
                  <span className="text-gray-600">Total Cost:</span>
                  <p className="font-bold text-patriot-red text-lg">
                    ${packages.find(p => p.id === selectedPackage)?.price}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="terms"
                required
                className="h-4 w-4 text-patriot-red focus:ring-patriot-red border-gray-300 rounded"
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <a href="#" className="text-patriot-red hover:text-patriot-red-dark">
                  Advertising Terms and Conditions
                </a>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                className="flex-1 bg-patriot-red text-white px-6 py-3 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 font-medium"
              >
                Launch Campaign
              </button>
              <button
                type="button"
                className="flex-1 border border-patriot-red text-patriot-red px-6 py-3 rounded-lg hover:bg-red-50 transition-colors duration-200 font-medium"
              >
                Save as Draft
              </button>
            </div>
          </form>
        </motion.div>

        {/* Support Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-12 bg-patriot-navy rounded-lg p-8 text-center text-white"
        >
          <h2 className="text-2xl font-bold mb-4">Need Help with Your Campaign?</h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Our campaign advertising experts are here to help you create effective ads 
            that resonate with conservative voters. Get personalized support for your campaign.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-patriot-red text-white px-6 py-3 rounded-lg hover:bg-patriot-red-dark transition-colors duration-200 font-medium">
              Schedule Consultation
            </button>
            <button className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white hover:text-patriot-navy transition-colors duration-200 font-medium">
              View Examples
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CandidateAdvertising;